import React from 'react';
import Lottie from 'lottie-react';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import {
	Button,
	IconCheckThick,
	IconLock,
	IconMessageSmall,
	Pro,
} from '@sololearnorg/sol';

import { Container, OwnershipRequirementType, i18n } from '../../../../symphony';
import { MaterialType, CompletionStatus, VisibilityStatus } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { IWidgetData } from '../../global-interface';
import { SlLEContext, getTypeNameMap } from '../../global-constants';
import { SlDataService } from '../../services/sl-data.service';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import lessonCheckmarkJson from './animations/Lesson_Checkmark.json';

import s from './Widget.scss';

const iconPathMap = {
	[MaterialType.CodeCoach]: 'le-cc-icon.svg',
	[MaterialType.CodeRepo]: 'le-cr-icon.svg',
	[MaterialType.CodeProject]: 'le-cp-icon.svg',
	[MaterialType.Lesson]: 'le-lesson-icon.svg',
	[MaterialType.ModuleQuiz]: 'le-module-quiz-icon.svg',
	[MaterialType.Booster]: 'le-solik-eyes-icon.svg',
};

interface IWidgetProps {
	parentType?: MaterialType;
	widget: IWidgetData;
	onClick?: () => void;
	id: number;
}

export function Widget({
	widget,
	onClick,
	parentType,
	id,
}: IWidgetProps): JSX.Element {
	useStyles(s);
	const {
		title,
		xp,
		commentsCount,
		status,
		courseOwnership,
		type,
		button,
		description,
		isCurrent,
	} = widget;

	const {
		lastCompletedMaterialIdInCurrentSession$: lastCompletedMaterialIdInCurrentSession,
		lastAvailableMaterialIdInCurrentSession$: lastAvailableMaterialIdInCurrentSession,
	} = useContainerData(SlLEContext, SlDataService, [
		'lastCompletedMaterialIdInCurrentSession$',
		'lastAvailableMaterialIdInCurrentSession$',
	]);

	const { cdnApiHostV2 } = Container.take('global', 'envUrl') as any;

	const isCompletedOrByPassed = (): boolean => (
		status.completion === CompletionStatus.Completed
		|| status.completion === CompletionStatus.ByPassed
	);

	const renderStatusIcon = (): JSX.Element => {
		if (id === lastCompletedMaterialIdInCurrentSession) {
			return (
				<Lottie
					animationData={lessonCheckmarkJson}
					autoPlay
					loop={false}
					className="le-widget__check-icon"
					width={40}
					height={40}
				/>
			);
		}

		if (isCompletedOrByPassed()) {
			return (
				<IconCheckThick
					sl-test-data="lblIconCheckThick"
					className="le-widget__check-icon"
					size={24}
				/>
			);
		}

		if (status?.ownership?.ownershipRequirementsTypeId === OwnershipRequirementType.Pro) {
			return <Pro size="s" />;
		}

		if (status.visibility === VisibilityStatus.Locked) {
			return (
				<IconLock
					sl-test-data="lblIconLock"
					className="le-widget__lock-icon"
					size={24}
				/>
			);
		}

		// if (
		// 	isCurrent
		// 	&& courseOwnership?.ownershipRequirementsTypeId === OwnershipRequirementType.Paid
		// 	&& !courseOwnership?.isOwned
		// 	&& status?.ownership?.ownershipRequirementsTypeId === OwnershipRequirementType.Free
		// ) {
		// 	return (
		// 		<div className="le-widget__free-icon">
		// 			{i18n.t('course.lesson.badge_free')}
		// 		</div>
		// 	);
		// }

		return null;
	};

	const onWidgetClickHandler = (): void => {
		if (isCompletedOrByPassed()) {
			onClick();
			return;
		}

		if (
			!isCurrent
			&& status.visibility === VisibilityStatus.Unlocked
			&& parentType !== MaterialType.LessonPage
		) {
			onClick();
		}

		// for locked cards in the course page
		if (
			status.visibility === VisibilityStatus.Locked
			&& parentType !== MaterialType.LessonPage
		) {
			onClick();
		}
	};

	const defineWidgetStatusClass = () => {
		if (isCompletedOrByPassed()) {
			return ' completed';
		}

		if (status.visibility === VisibilityStatus.Locked) {
			return ' locked';
		}

		if (isCurrent && status.visibility === VisibilityStatus.Unlocked) {
			return ' current';
		}

		if (parentType === MaterialType.LessonPage) {
			return ' current';
		}

		return '';
	};

	if (parentType === MaterialType.LessonPage) {
		return (
			<div
				className={classNames('le-lesson-widget', defineWidgetStatusClass())}
				onClick={onWidgetClickHandler}
			>
				<div className="le-lesson-widget__container">
					<img
						src={
							widget?.iconUrl
								? widget.iconUrl
								: `${cdnApiHostV2}/web-assets/${iconPathMap[type]}`
						}
						className="le-lesson-widget__main-icon"
						alt="widgetImg"
					/>

					<div className="le-lesson-widget__content">
						<div className="le-lesson-widget__title">{title}</div>
						<div className="le-lesson-widget__description">
							{description}
						</div>
						{!isCompletedOrByPassed() && (
							<div className="le-lesson-widget__chips-box">
								{xp && (
									<div className="le-lesson-widget__chip">
										<span className="le-lesson-widget__chip-icon">
											XP
										</span>
										+
										{xp}
									</div>
								)}
							</div>
						)}
					</div>

					<div className="le-lesson-widget__status">
						{renderStatusIcon()}
					</div>
				</div>

				{!isCompletedOrByPassed() && (
					<Button
						className="le-lesson-widget__action-button"
						onClick={button.onClick}
					>
						{button.title}
					</Button>
				)}
			</div>
		);
	}

	if (parentType === MaterialType.Module) {
		return (
			<div
				className={classNames('le-course-widget', defineWidgetStatusClass(), {
					animated: id === lastAvailableMaterialIdInCurrentSession,
				})}
				onClick={onWidgetClickHandler}
			>
				<div className="le-course-widget__container">
					<img
						src={`${cdnApiHostV2}/web-assets/${iconPathMap[type]}`}
						className="le-course-widget__main-icon"
						alt="widgetImg"
					/>

					<div className="le-course-widget__content">
						<div className="le-course-widget__order">
							{getTypeNameMap()[type]}
						</div>
						<div className="le-course-widget__title">{title}</div>
						{!isCompletedOrByPassed() && (
							<div className="le-course-widget__chips-box">
								{commentsCount && (
									<div className="le-course-widget__chip le-course-widget__chip--commentsCount">
										<div className="le-course-widget__chip-icon">
											<IconMessageSmall size={16} />
										</div>
										{commentsCount}
									</div>
								)}
								{xp && (
									<div className="le-course-widget__chip">
										<span className="le-course-widget__chip-icon">
											XP
										</span>
										+
										{xp}
									</div>
								)}
							</div>
						)}
					</div>

					<div className="le-course-widget__status">
						{renderStatusIcon()}
					</div>
				</div>

				{!isCompletedOrByPassed() && isCurrent && (
					<Button
						className={classNames('le-course-widget__action-button', {
							animated: id === lastAvailableMaterialIdInCurrentSession,
						})}
						onClick={button.onClick}
						buttonType={button.paid ? 'aiprimary' : 'primary'}
					>
						{button.title}
					</Button>
				)}
			</div>
		);
	}

	return null;
}
